import { TipoAtendimentoProfissional, TipoParticipacaoAtendimentoEnum } from 'graphql/types.generated'
import { createValidator, ErrorObject, required, requiredFor, ValidatorFunction } from 'util/validation'
import { isEmpty } from 'util/validation/Util'

import { DesfechoAtendimentoFormModel, ManterCidadaoLista } from '../components/DesfechoAtendimentoPanel'
import { MSG_ERRO_PARTICIPACAO_SEM_PROFISSIONAL } from '../constants-finalizacao'
import { FinalizacaoAtendimentoFormModel } from '../FinalizacaoAtendimentoForm'
import { DescricaoProcedimentosTeleconsulta, isCondutaRequired, ObrigatoriedadesParticipacao } from '../util'
import { agendarConsultaAvulsaValidator } from './validator-agendamento'
import { finalizacaoOdontoValidator } from './validator-odonto'

export const finalizacaoIndividualValidator = (
  isObservacao: boolean,
  hasEncaminhamentoExterno: boolean,
  getServerTimeNow: () => Date,
  showHorarioFinal: boolean
) =>
  createValidator<FinalizacaoAtendimentoFormModel>(
    {
      tipoAtendimento: [required],
      agendamentoConsultas: agendarConsultaAvulsaValidator(getServerTimeNow, showHorarioFinal),
    },
    (values: FinalizacaoAtendimentoFormModel, errors: ErrorObject<FinalizacaoAtendimentoFormModel>) => {
      const isRequired = isCondutaRequired({
        isObservacao,
        hasNotificacaoCasoSuspeito: !isEmpty(values.fichasNotificacaoCasoSuspeito),
        hasEncaminhamentoExterno,
        manterCidadaoNaLista: values.desfechoAtendimento?.manterCidadaoLista,
      })
      if (isRequired) {
        errors.conduta = required(values.conduta)
      }
      return errors
    }
  )

const desfechoProcedimentosValidator = (getServerTimeNow: () => Date, showHorarioFinal: boolean) =>
  createValidator<FinalizacaoAtendimentoFormModel>({
    agendamentoConsultas: agendarConsultaAvulsaValidator(getServerTimeNow, showHorarioFinal),
  })

const desfechoObservacaoValidator = (isAtendimentoObservacao: boolean, podeFinalizarAtendimentoObservacao: boolean) =>
  createValidator<FinalizacaoAtendimentoFormModel>(
    {},
    (values: FinalizacaoAtendimentoFormModel, errors: ErrorObject<FinalizacaoAtendimentoFormModel>) => {
      if (
        isAtendimentoObservacao &&
        !podeFinalizarAtendimentoObservacao &&
        values.desfechoAtendimento.manterCidadaoLista === ManterCidadaoLista.NAO
      ) {
        ;(errors.desfechoAtendimento as ErrorObject<DesfechoAtendimentoFormModel>).manterCidadaoLista =
          'Somente médicos e enfermeiros podem liberar o cidadão.'
      }

      if (
        isAtendimentoObservacao &&
        podeFinalizarAtendimentoObservacao &&
        values.desfechoAtendimento.manterCidadaoLista === ManterCidadaoLista.NAO
      ) {
        errors.conduta = required(values.conduta)
      }

      return errors
    }
  )

export const finalizacaoAtendimentoValidator = (
  tipoAtendProf: TipoAtendimentoProfissional,
  isDemandaEspontanea: boolean,
  hasEncaminhamentoExterno: boolean,
  isStartObservacao: boolean,
  isAtendimentoObservacao: boolean,
  isResponsavelAtendimentoObservacao: boolean,
  podeFinalizarAtendimentoObservacao: boolean,
  getServerTimeNow: () => Date,
  showHorarioFinal: boolean,
  obrigatoriedadesParticipacao: ObrigatoriedadesParticipacao,
  descricaoProcedimentosTeleconsulta: DescricaoProcedimentosTeleconsulta
) => {
  const adicionarValidacaoParticipacaoTeleconsulta = (
    baseValidator: ValidatorFunction<FinalizacaoAtendimentoFormModel>
  ) => {
    return (values: FinalizacaoAtendimentoFormModel) => {
      const errors = baseValidator?.(values) || {}

      if (obrigatoriedadesParticipacao.isParticipacaoCidadao || obrigatoriedadesParticipacao.isParticipacaoAmbos) {
        errors.tipoParticipacaoCidadao = requiredFor(
          values.tipoParticipacaoCidadao,
          'procedimento',
          descricaoProcedimentosTeleconsulta.descricaoProcedimentosCidadaoEAmbos
        )
      }
      if (obrigatoriedadesParticipacao.isParticipacaoProfissional || obrigatoriedadesParticipacao.isParticipacaoAmbos) {
        errors.tipoParticipacaoProfissionalConvidado = requiredFor(
          values.tipoParticipacaoProfissionalConvidado,
          'procedimento',
          descricaoProcedimentosTeleconsulta.descricaoProcedimentosProfissionalEAmbos
        )
      }

      const hasProfissionalConvidado = !!values.atendimentoCompartilhadoLotacao || !!values.profissionalNaoListado
      const hasFormaParticipacaoProfissional =
        !!values.tipoParticipacaoProfissionalConvidado &&
        values.tipoParticipacaoProfissionalConvidado !== TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU

      if (!hasProfissionalConvidado && hasFormaParticipacaoProfissional) {
        errors.tipoParticipacaoProfissionalConvidado = MSG_ERRO_PARTICIPACAO_SEM_PROFISSIONAL
      }

      return errors
    }
  }

  switch (tipoAtendProf) {
    case TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO: {
      const validator = finalizacaoOdontoValidator(
        isDemandaEspontanea,
        hasEncaminhamentoExterno,
        getServerTimeNow,
        showHorarioFinal
      )
      return adicionarValidacaoParticipacaoTeleconsulta(validator)
    }
    case TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS: {
      return desfechoProcedimentosValidator(getServerTimeNow, showHorarioFinal)
    }
    case TipoAtendimentoProfissional.OBSERVACAO: {
      if (isResponsavelAtendimentoObservacao) {
        const validator = finalizacaoIndividualValidator(
          isAtendimentoObservacao,
          hasEncaminhamentoExterno,
          getServerTimeNow,
          showHorarioFinal
        )
        return adicionarValidacaoParticipacaoTeleconsulta(validator)
      }
      return desfechoObservacaoValidator(isAtendimentoObservacao, podeFinalizarAtendimentoObservacao)
    }
    default: {
      const isObservacao = isStartObservacao || isAtendimentoObservacao
      const validator = finalizacaoIndividualValidator(
        isObservacao,
        hasEncaminhamentoExterno,
        getServerTimeNow,
        showHorarioFinal
      )
      return adicionarValidacaoParticipacaoTeleconsulta(validator)
    }
  }
}
